import { Auth, CognitoUser } from "@aws-amplify/auth";
import awsmobile from "../aws-exports";
import React, { PropsWithChildren } from "react";
Auth.configure(awsmobile);

interface AuthState {
  signIn: (options: { username: string; password: string }) => Promise<any>;
  signUp: (options: {
    username: string;
    password: string;
    email?: string;
  }) => Promise<any>;
  signOut: () => Promise<void>;
  isAuthenticated: () => boolean;
  user: any;
}

const defaultAuthState: AuthState = {
  signIn: (options) => Promise.resolve(),
  signUp: (options) => Promise.resolve(),
  signOut: () => Promise.resolve(),
  isAuthenticated: () => false,
  user: null,
};

const AuthContext = React.createContext(defaultAuthState);

export const useAuth = () => React.useContext(AuthContext);

export const AuthContextProvider: React.FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [user, setUser] = React.useState<CognitoUser | null>(null);
  React.useEffect(() => {
    Auth.currentAuthenticatedUser().then(setUser, (e) => setUser(null));
  }, []);

  const isAuthenticated = () => {
    return !!user;
  };

  const signIn = async ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    return Auth.signIn(username, password).then((user) => {
      setUser(user);
      return user;
    });
  };

  const signOut = async () => {
    await Auth.signOut();
    return setUser(null);
  };

  const signUp = async ({
    username,
    password,
    email,
  }: {
    username: string;
    password: string;
    email?: string;
  }) => {
    return await Auth.signUp({
      username,
      password,
      autoSignIn: {
        enabled: true,
      },
      attributes: {
        email,
      },
    }).then((result) => {
      setUser(result.user);
    });
  };

  const result = { user, isAuthenticated, signIn, signOut, signUp };
  return <AuthContext.Provider value={result}>{children}</AuthContext.Provider>;
};
